import { FieldRenderers, FormField } from "@avamae/formbuilder";
import { accessRightsRenderer } from "components/AccessRights/AccessRights";
import { booleanV2Renderer } from "components/BooleanV2/BooleanV2";
import { dateTimeV2Renderer } from "components/DateTimeFieldV2/DateTimeFieldV2";
import Entity from "components/Entity";
import { exchangeRatesTableRenderer } from "components/ExchangeRatesTable/ExchangeRatesTable";
import { booleanCheckboxRenderer } from "components/helpers/CustomFieldRenderers/BooleanCheckbox/BooleanCheckbox";
import { stringReadOnlyEditRenderer } from "components/helpers/CustomFieldRenderers/CustomString/StringReadOnlyEditFieldRenderer";
import { searchListFieldRenderer } from "components/helpers/CustomFieldRenderers/SearchListFieldRenderer/SearchListRenderer";
import {
  singleMultiSelectFieldRenderer,
  singleMultiSelectReadOnlyFieldRenderer,
  singleMultiSelectWithLinkReadOnlyFieldRenderer,
} from "components/helpers/CustomFieldRenderers/SingleMultiSelectFieldRenderer.tsx/SingleMultiSelectFieldRenderer";
import { textAreaFieldRenderer } from "components/helpers/CustomFieldRenderers/TextAreaFieldRenderer/TextAreaFieldRenderer";
import { fileUploadFieldRenderer } from "components/helpers/FieldRenderers/FileUpload/fileUploadFieldRenderer";
import { APP_ROUTES, endpoints } from "config";
import { useField, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { CustomFileViewer } from "pages/Documents/helpers/FileViewer";
import {
  actionLinkTextConfig,
  endpointConfig,
  linkConfig,
  onActionLinkClick,
  onLinkClick,
} from "./config";
import { Modal } from "components/modal/Modal";
import { InvoiceFieldsDetails } from "pages/Invoices/Invoice/Invoice.types";
import { actionTypesWithUpdateDocumentPermissions } from "pages/Invoices/Invoice/Invoice.config";
import {
  rightsRendererUpdateWithUpdateDocumentPermissions,
  rightsRendererUpdateWithoutUpdateDocumentPermissions,
} from "pages/Invoices/Invoice/Invoice.renderers";
import { getInvoicesEndpointsPermissions } from "pages/Invoices/InvoicesList.config";
import { useWindowSize } from "helpers/useWindowSize";
import { ColumnsMaxWidths } from "components/Entity/Entity";

import { useDispatch } from "react-redux";
import { setRefreshAllFormData } from "reducers/entity";
import { parseListDate } from "helpers/parseListDate";

type RoutingTableProps = {
  fieldName: string;
  label: string;
  readOnly?: boolean;
  idFieldName?: string;
  hideReviewButton?: boolean;
};

type ExchangeRateData = {
  routedOn: string;
  routedBy: string;
  routedTo: string;
  status: string;
};

const RoutingTable: React.FC<RoutingTableProps> = ({
  fieldName,
  label,
  readOnly,
  idFieldName,
  hideReviewButton,
}) => {
  const [viewPanelOpen, setViewPanelOpen] = useState<boolean>(false);
  const [selectedViewId, setSelectedViewId] = useState<number | null>(null);
  const [_, meta, helpers, ...rest] = useField<ExchangeRateData[]>(fieldName);
  const [id, setId] = useState<number | null>(null);
  const [refreshViewFormData, setRefreshViewFormData] = useState<boolean>(false);
  const [value, setValue] = useState<ExchangeRateData[] | null>(null);

  useEffect(() => {
    if (refreshViewFormData) {
      dispatch(setRefreshAllFormData(true));
      setRefreshViewFormData(false);
    }
  }, [refreshViewFormData]);

  const formik = useFormikContext();

  useEffect(() => {
    if (formik.values && formik.values.hasOwnProperty(fieldName)) {
      const data: any = formik.values[fieldName as keyof typeof formik.values];

      if (data && Array.isArray(data) && data.length > 0) {
        setValue(data);
      } else {
        setValue(null);
      }
    }
  }, [formik?.values]);

  useEffect(() => {
    if (
      readOnly &&
      idFieldName &&
      formik &&
      formik.values &&
      formik.values.hasOwnProperty(idFieldName)
    ) {
      setId(formik.values[idFieldName as keyof typeof formik.values]);
    }
  }, [formik?.values]);

  const handleReviewClick = (id: number) => {
    setSelectedViewId(id);
    setViewPanelOpen(true);
  };

  const dispatch = useDispatch();

  return (
    <div className={`RoutingTableContainer ${readOnly ? "ReadOnly" : ""}`}>
      {value && value.length > 0 && (
        <div className="RoutingTable">
          <div className="TableHeader Row">
            <div className="Column">
              <div className="Text">Routed on</div>
            </div>
            <div className="Column">
              <div className="Text">Routed by</div>
            </div>
            <div className="Column">
              <div className="Text">Routed to</div>
            </div>
            <div className="Column">
              <div className="Text">Status</div>
            </div>
          </div>

          {value.map((item) => {
            return (
              <div className="Row">
                <div className="Column">
                  <div className="Text">
                    {item.routedOn ? parseListDate(item.routedOn, true) : "-"}
                  </div>
                </div>
                <div className="Column">
                  <div className="Text">{item.routedBy ?? "-"}</div>
                </div>
                <div className="Column">
                  <div className="Text">{item.routedTo ?? "-"}</div>
                </div>
                <div className="Column">
                  <div className="Text">{item.status ?? "-"}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {(!value || (value && value.length === 0)) && (
        <div className="NoData">
          <div className="FormLabel">Routing</div>

          <div className="Text">No Data</div>
        </div>
      )}

      {readOnly && typeof id === "number" && !hideReviewButton && (
        <div className="Column IdColumn">
          <button className="Btn ReviewRouteBtn" onClick={() => handleReviewClick(id)}>
            <div className="Text">Review</div>
          </button>
        </div>
      )}

      {viewPanelOpen && selectedViewId && (
        <Modal
          open={viewPanelOpen}
          closeModal={() => {
            setRefreshViewFormData(true);
            setSelectedViewId(null);
            setViewPanelOpen(false);
          }}
          title={`View details`}
          hideFooterButtons={true}
        >
          <ViewPanel
            id={selectedViewId}
            onDone={() => {
              setRefreshViewFormData(true);
              setSelectedViewId(null);
              setViewPanelOpen(false);
            }}
          />
        </Modal>
      )}
    </div>
  );
};

export const routingTableRenderer = (
  readOnly: boolean,
  idFieldName?: string,
  hideReviewButton?: boolean
) =>
  function routingTableRenderer<T>(_key: React.ReactText, field: FormField<T>) {
    return (
      <RoutingTable
        label={field.label as string}
        readOnly={readOnly}
        fieldName={field.name as string}
        idFieldName={idFieldName ?? "id"}
        hideReviewButton={hideReviewButton ?? false}
      />
    );
  };

type ViewPanelProps = {
  id: number;
  onDone: () => void;
};

interface RoutingView {
  id: number | null;
}

import { optionalNumber } from "helpers/validation/yupSchemaFactory";

const validationSchema = Yup.object({
  id: optionalNumber(),
});

const readOnlyRightsRendererViewUpdate = (): FieldRenderers<InvoiceFieldsDetails> => ({
  accessrights: accessRightsRenderer(true),

  multiselectsearchlistwithlinks: singleMultiSelectWithLinkReadOnlyFieldRenderer({
    listAssociatedUsers: {
      filterFieldName: "Invoices_InvoiceIDs",
      filterOperator: "CONTAINS",
      filterValueFieldName: "id",
      text: "View all users",
      route: APP_ROUTES.platformUsers,
    },
  }),
  // multiselectsearchlistwithlinks: searchListFieldRenderer("multiselect"),
  singleselectreadonly: singleMultiSelectFieldRenderer(
    "singleselect",
    undefined,
    undefined,
    undefined,
    true
  ),
  multiselectreadonly: singleMultiSelectFieldRenderer(
    "multiselect",
    undefined,
    undefined,
    undefined,
    true
  ),
  singleselectsearchlist: searchListFieldRenderer("singleselect", undefined, null, true),
  singleselectsearchlistreadonly: searchListFieldRenderer(
    "singleselect",
    undefined,
    null,
    false,
    true
  ),
  singleselectsearchlistwithlinksreadonly: searchListFieldRenderer(
    "singleSelectWithLinks",
    {
      searchEndpoint: endpointConfig,
      linkTextConfig: linkConfig,
      onLinkClick: onLinkClick,
      actionLinkConfig: actionLinkTextConfig,
      handleActionLinkClicked: onActionLinkClick,
    },
    undefined,
    true
  ),
  multiselectsearchlistwithlinksreadonly: searchListFieldRenderer(
    "multiselectWithLinks",
    {
      searchEndpoint: endpointConfig,
      linkTextConfig: linkConfig,
      onLinkClick: onLinkClick,
      actionLinkConfig: actionLinkTextConfig,
      handleActionLinkClicked: onActionLinkClick,
    },
    undefined,
    true
  ),
  datetimereadonly: dateTimeV2Renderer(false, true),
  datetime: dateTimeV2Renderer(true),

  singleselectsearchlistwithlinks: singleMultiSelectReadOnlyFieldRenderer("multiselect"),
  textarea: textAreaFieldRenderer(true),
  file: fileUploadFieldRenderer(true),
  stringreadonly: stringReadOnlyEditRenderer(null, null, false),
  integerreadonly: stringReadOnlyEditRenderer(null, null, true),
  numberreadonly: stringReadOnlyEditRenderer(null, null, true),
  decimalreadonly: stringReadOnlyEditRenderer(null, null, true),
  booleanreadonly: booleanV2Renderer(false, undefined, undefined, true),
  // pdfviewercolumn: pdfViewerColumnRenderer(true),
  pdfviewercolumn: (_key, field) => (
    <CustomFileViewer
      field={field}
      readOnly={true}
      rightColumnViewer={true}
      bHasUpdateDocumentPermissions={false}
      newFileFieldName={"file"}
    />
  ),
  exchangeratestable: exchangeRatesTableRenderer(true),
  routingtable: routingTableRenderer(true, "id"),
  booleancheckbox: booleanCheckboxRenderer(true),
});

const ViewPanel: React.FC<ViewPanelProps> = ({ id, onDone }) => {
  let finalRightsRenderer = rightsRendererUpdateWithoutUpdateDocumentPermissions;

  const endpointsPermissions = getInvoicesEndpointsPermissions("invoices");

  if (
    endpointsPermissions &&
    endpointsPermissions.edit &&
    actionTypesWithUpdateDocumentPermissions.includes(endpointsPermissions.edit)
  ) {
    // NOTE: Does have update document permissions
    finalRightsRenderer = rightsRendererUpdateWithUpdateDocumentPermissions;
  }

  let initialDataUrl = "";

  if (endpointsPermissions.edit === "edit") {
    initialDataUrl = endpoints.invoices.edit;
  } else if (endpointsPermissions.edit === "update") {
    initialDataUrl = endpoints.invoices.edit;
  } else if (endpointsPermissions.edit === "update-apclerk") {
    initialDataUrl = endpoints.invoices.editApClerk;
  } else if (endpointsPermissions.edit === "update-apcoordinator") {
    initialDataUrl = endpoints.invoices.editFinance;
  } else if (endpointsPermissions.edit === "update-productmanager") {
    initialDataUrl = endpoints.invoices.editProjectManager;
  }

  const [columnsMaxWidths, setColumnsMaxWidths] = useState<ColumnsMaxWidths | null>(null);

  const [width, height] = useWindowSize(); // Listen for window resizing

  useEffect(() => {
    if (width <= 900) {
      setColumnsMaxWidths({ 1: "100%" });
    } else if (width > 900) {
      setColumnsMaxWidths({ 1: "60%" });
    }
  }, [width]);

  return (
    <>
      <Entity
        actionType="update"
        initialDataUrl={initialDataUrl}
        readOnly={false}
        submitUrl={initialDataUrl}
        onDone={onDone}
        selectedData={[{ invoices__Id: id }]}
        idFromRowOption={id}
        formType="multipart"
        hiddenFields={["id"]}
        readOnlyFields={[
          {
            fieldName: "Id",
            readOnlyRenderer: "integerreadonly",
          },
        ]}
        fieldRenderers={finalRightsRenderer}
        validationSchema={validationSchema}
        readOnlyFieldRenderers={readOnlyRightsRendererViewUpdate}
        nullStringValues={[]}
        fetchConfig={{
          params: {
            Id: id,
          },
        }}
        hideActionButton={false}
        noSubmit={false}
        wrapperClassName="InvoicesReview"
        groupColumns={undefined}
        groupsWithBorders={[{ groupName: "Money", groupBorderColour: "#d5d5d5" }]}
        columnsMaxWidths={columnsMaxWidths ?? undefined}
        groupSubColumns={{
          Details: 3,
          Money: 3,
          Approved: 3,
          Notes: 3,
        }}
        conditionalFields={{
          AssignedToID: {
            dependentField: "BAssignToFinanceRole",
            dependentValues: [false], // 1001 = "Other user"
          },
        }}
        hideSpecificGroupLabels={["Details", "Money", "Notes", "Approved"]}
        hideViewGroupLabel={true}
        rightSectionFieldType={"PdfViewerColumn"}
      />
    </>
  );
};
